import {
  CollectionShare,
  collectionQueryOptions,
  fetchCollectionPdf,
  useCollectionDeleteMutation,
  useCollectionShareUpdateMutation,
  useCollectionUpdateMutation,
} from '@blesk-frontend/api';
import {Button, ModalConfirm, Page, Topbar, Trash, useModal} from '@blesk-frontend/ui';
import {useQuery} from '@tanstack/react-query';
import {Link, useNavigate, useParams} from '@tanstack/react-router';
import {ChevronLeft} from 'lucide-react';
import {useState} from 'react';
import {toast} from 'sonner';

import {GuideSkeleton} from '@/features/guides';
import {sendEvent} from '@/libs/analytics';
import {CollectionFillButton, CollectionShareMenu, CollectionView} from '../components';

export const CollectionDetailPage = () => {
  const navigate = useNavigate();
  const params = useParams({from: '/_app/collections/$collectionId/'});
  const {data: collection, isLoading} = useQuery(collectionQueryOptions(params.collectionId));
  const {
    mutateAsync: mutateAsyncShare,
    variables: optimisticShare,
    isPending: isPendingShare,
  } = useCollectionShareUpdateMutation();
  const {mutateAsync: collectionDeleteMutateAsync} = useCollectionDeleteMutation();

  const {mutateAsync: collectionUpdateMutateAsync} = useCollectionUpdateMutation();

  const openConfirmDeleteModal = useModal((props) => (
    <ModalConfirm
      {...props}
      title="Подтвердите удаление"
      message="Подтвердите, что вы точно хотите удалить выбранную коллекцию."
      labels={{cancel: 'Отменить', confirm: 'Удалить'}}
      onConfirm={async () => {
        collection && (await collectionDeleteMutateAsync(collection.id));
        await navigate({to: '/collections'});
      }}
    />
  ));

  const onUpdateShare = async (type: CollectionShare['type']) => {
    if (!collection) return;
    await mutateAsyncShare({collectionId: collection.id, type});
  };

  const [isDownloading, setIsDownloading] = useState(false);

  const onClickDownload = async () => {
    if (!collection) return;
    if (!collection.guides.length) {
      toast.error('Нельзя скачать пустую коллекцию');
      return;
    }

    toast.loading('Скачивание...', {id: 'download-collection'});
    setIsDownloading(true);
    await fetchCollectionPdf(collection.id)
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response]));
        const a = document.createElement('a');
        a.href = url;
        a.download = `${collection.name}.zip`;
        document.body.appendChild(a);
        a.click();
      })
      .catch((error) => {
        console.error(error);
        toast.error('Ошибка при скачивании коллекции', {id: 'download-collection'});
      })
      .finally(() => {
        setIsDownloading(false);
      });
    toast.dismiss('download-collection');
    sendEvent('collections/export-pdf');
  };

  if (isLoading || !collection) {
    return <GuideSkeleton />;
  }

  return (
    <Page className="relative h-full">
      <Topbar variant="sticky">
        <Link
          to="/collections"
          className="inline-flex items-center gap-1 text-paragraph-sm text-text-secondary"
        >
          <Button variant="outline" size="icon">
            <ChevronLeft className="size-4" />
          </Button>
        </Link>

        {collection.deletedAt ? (
          <div className="ml-auto text-text-secondary">
            <Button variant="outline">Восстановить</Button>
          </div>
        ) : (
          <div className="ml-auto flex items-center gap-4 text-text-secondary">
            {!!collection.guides.length && <CollectionFillButton collection={collection} />}

            <CollectionShareMenu
              share={isPendingShare ? optimisticShare : collection.share}
              onUpdate={onUpdateShare}
            />
            <Button
              variant="outline"
              onClick={onClickDownload}
              disabled={isDownloading}
              loading={isDownloading}
            >
              Скачать
            </Button>

            <Button variant="outline" size="icon" onClick={() => openConfirmDeleteModal()}>
              <Trash className="size-5" />
            </Button>
          </div>
        )}
      </Topbar>

      <CollectionView
        collection={collection}
        onSubmit={async (data) => {
          await collectionUpdateMutateAsync({id: collection.id, ...data});
        }}
      />
    </Page>
  );
};
